// TelcoTrack — Tabulka ROPID (CSV export) → mapování na BUDOVA import.
//
// Zdroj: docs/infrastructure/export_tables/Tabulka_ROPID.csv (UTF-8 s BOM nebo cp1250, oddělovač „;“).
// Tabulka ROPID export: sloupce jsou prohozené a kladné — „Souřadnice Y“ = východ (|X|),
// „Souřadnice X“ = sever (|Y|). Import neguje na PostGIS EPSG:5514 (záporné Krovak hodnoty).

import { normalizeAcquisitionText } from './acquisition-report.constants';
import type { TypCisla } from '../utils/typ-cisla.util';

/** Jeden řádek ROPID tabulky z webu (hlavička → hodnota). */
export type RopidRawRow = Record<string, string>;

/** Kanonická pole po mapování z CSV. */
export interface RopidImportFields {
  kia?: string;
  ropId?: string;
  stavRop?: string;
  celaAdresa?: string;
  cisloPopisne?: string;
  /** Typ čísla domu z „Celá adresa“ („č.ev. 2“ → `E`); chybí → neurčeno. */
  typCisla?: TypCisla;
  /** Ulice z „Celá adresa“ (např. Na Návsi), pokud je v CSV. */
  ulice?: string;
  mesto?: string;
  psc?: string;
  hpCountRop?: number;
  hpCountObject?: number;
  typDomu?: string;
  bjCount?: number;
  /** S-JTSK northing (ST_Y, EPSG:5514 — záporné v ČR). */
  coordY?: number;
  /** S-JTSK easting (ST_X, EPSG:5514 — záporné v ČR). */
  coordX?: number;
  kiaAssignedAt?: string;
}

export interface RopidMappedRow {
  fields: RopidImportFields;
  /** Kanonický klíč → text z reportu (pro raw_data / audit). */
  raw: Record<string, string>;
  rowIndex: number;
}

/** Normalizované hlavičky CSV → kanonické pole. */
export const ROPID_HEADER_ALIASES: Record<keyof RopidImportFields, string[]> = {
  kia: ['kia'],
  ropId: ['rop id'],
  stavRop: ['stav rop'],
  celaAdresa: ['cela adresa'],
  cisloPopisne: ['cislo popisne', 'adresa'],
  // Typ čísla není samostatný sloupec CSV — odvozuje se z „Celá adresa“.
  typCisla: [],
  ulice: ['ulice'],
  mesto: ['mesto'],
  psc: ['psc'],
  hpCountRop: ['pocet hp na rop v ik0', 'pocet ik0 hp'],
  hpCountObject: ['pocet hp na objektu v ik0', 'pocet hp objekt'],
  typDomu: ['typ domu'],
  bjCount: ['pocet bj'],
  coordY: ['souradnice y'],
  coordX: ['souradnice x'],
  kiaAssignedAt: ['datum prirazeni kia k ropid'],
};

/** Typické rozsahy PostGIS EPSG:5514 pro validaci (ČR, záporné Krovak souřadnice). */
export const ROPID_SJTSK_BOUNDS = {
  minX: -1_000_000,
  maxX: -400_000,
  minY: -1_350_000,
  maxY: -900_000,
} as const;

export function normalizeRopId(value: string | null | undefined): string {
  return normalizeAcquisitionText(value).replace(/[^a-z0-9]/g, '');
}

/** Maximální vzdálenost (m) pro spatial fallback ROPID → RUIAN `address_point` v polygonu. */
export { ADDRESS_ASSIGNMENT_MAX_DISTANCE_M as ROPID_ADDRESS_SPATIAL_MAX_DISTANCE_M } from './address-assignment.constants';
